import React, { useEffect, useState } from 'react'
import {
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useOutlet,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { AUTH_CHANGE_EVENT, isLoggedIn } from './api/auth'
import { invalidate, useMe } from './api/hooks'
import { AnimatePresence, motion } from 'framer-motion'
import { DiscoverPage } from './components/DiscoverPage'
import { ProfilePage } from './components/ProfilePage'
import { BottomNav } from './components/BottomNav'
import { ShowcaseArtistPage } from './components/showcase/ShowcaseArtistPage'
import { ShowcaseIndexPage } from './components/showcase/ShowcaseIndexPage'
import { ShowcaseLayout } from './components/showcase/ShowcaseLayout'
import { AuthCallbackPage } from './components/AuthCallbackPage'
import { GoogleUsernamePage } from './components/GoogleUsernamePage'
import { SHOWCASE_ENABLED } from './showcase/config'
import {
  RegisterPage,
  RegisterLanding,
  RegisterAuthFormSignup,
  RegisterAuthFormLogin,
} from './components/RegisterPage'
import { StringDetailModal } from './components/StringDetailModal'

export interface StringItem {
  id: string
  content: string
  author: string
  authorUsername: string
  authorProfileImageUrl?: string | null
  time: string
  date: string
}

// IMPORTANT: keep these to opacity-only. Any `x`/`y`/scale here would put
// `transform` on the wrapper, which establishes a containing block and
// breaks `position: fixed` for descendants (BottomNav, DiscoverPage <nav>).
const pageVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
}

const pageTransition = {
  type: 'tween' as const,
  ease: [0.25, 0.46, 0.45, 0.94],
  duration: 0.28,
}

function AnimatedMainRoutes() {
  const location = useLocation()
  const outlet = useOutlet()

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="enter"
        animate="center"
        exit="exit"
        transition={pageTransition}
      >
        {outlet}
      </motion.div>
    </AnimatePresence>
  )
}

function MainApp() {
  const routerNavigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState<'discover' | 'profile'>(() =>
    searchParams.get('view') === 'profile' ? 'profile' : 'discover',
  )
  const [selectedString, setSelectedString] = useState<StringItem | null>(null)
  const [loggedIn, setLoggedIn] = useState(() => isLoggedIn())

  useEffect(() => {
    const syncAuth = () => setLoggedIn(isLoggedIn())
    window.addEventListener(AUTH_CHANGE_EVENT, syncAuth)
    return () => window.removeEventListener(AUTH_CHANGE_EVENT, syncAuth)
  }, [])

  const { data: me } = useMe(loggedIn)
  useEffect(() => {
    if (me?.needsUsername) {
      routerNavigate('/register/google-username', { replace: true })
    }
  }, [me, routerNavigate])

  useEffect(() => {
    const next = searchParams.get('view') === 'profile' ? 'profile' : 'discover'
    setCurrentPage(next)
  }, [searchParams])

  const navigate = (page: 'discover' | 'profile') => {
    setCurrentPage(page)
    setSearchParams(page === 'profile' ? { view: 'profile' } : {})
  }

  const navigateToUserProfile = (username: string) => {
    const normalized = username.trim().replace(/^@/, '')
    if (!normalized) return
    setCurrentPage('profile')
    setSearchParams({ view: 'profile', user: normalized })
  }

  return (
    <>
      <AnimatePresence mode="wait">
        {currentPage === 'discover' && (
          <motion.div
            key="discover"
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={pageTransition}
          >
            <DiscoverPage
              onNavigate={navigate}
              onSelectString={setSelectedString}
            />
          </motion.div>
        )}
        {currentPage === 'profile' && (
          <motion.div
            key="profile"
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={pageTransition}
          >
            <ProfilePage onSelectString={setSelectedString} />
          </motion.div>
        )}
      </AnimatePresence>
      <BottomNav currentPage={currentPage} onNavigate={navigate} />
      <AnimatePresence>
        {selectedString && (
          <StringDetailModal
            string={selectedString}
            onClose={() => {
              setSelectedString(null)
              invalidate.feed()
            }}
            onNavigateToProfile={navigateToUserProfile}
          />
        )}
      </AnimatePresence>
    </>
  )
}

export function App() {
  return (
    <Routes>
      <Route path="/register" element={<RegisterPage />}>
        <Route index element={<RegisterLanding />} />
        <Route path="sign-up" element={<RegisterAuthFormSignup />} />
      </Route>
      <Route path="/login" element={<RegisterPage />}>
        <Route index element={<RegisterAuthFormLogin />} />
      </Route>
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/register/google-username" element={<GoogleUsernamePage />} />
      {SHOWCASE_ENABLED ? (
        <Route element={<ShowcaseLayout />}>
          <Route path="/showcase" element={<ShowcaseIndexPage />} />
          <Route path="/showcase/:artistSlug" element={<ShowcaseArtistPage />} />
        </Route>
      ) : (
        <Route path="/showcase/*" element={<Navigate to="/" replace />} />
      )}
      <Route path="/" element={<AnimatedMainRoutes />}>
        <Route index element={<MainApp />} />
      </Route>
    </Routes>
  )
}
